/**
 * @ken/module-services — frontend entry point.
 *
 * Mirrors @ken/module-suppliers + @ken/module-products shape.
 * The Service canonical entity (ADR-0073) is one of the per-concept
 * tables split off from the polymorphic QB Items List. This module
 * owns the cockpit pages for it.
 *
 * The in-list detail Sheet is RETIRED for services in favour of the full-page
 * STORY route (ADR-0081/0082, PARTY archetype — a master/catalog record with a
 * catalog stat strip). Clicking a service row navigates to
 * `services/$id/overview`; the parent `services/$id` renders the
 * StoryPageDetail shell + routed tab children (overview / related / notes) with
 * an index redirect to overview. Mirrors the products story rollout.
 */

import {
  lazyScreen,
  type AnyRoute,
  type CreateModuleRoutes,
} from "@ken/cockpit-types";
import { createRoute, redirect } from "@tanstack/react-router";

import type { ServiceStorySearch } from "./routes/story/service-story-detail";

// One loader per screen file, shared by every screen that file exports, so the
// route table carries each file's import (and its preload list) once (SF-BUNDLE).
const loadServiceStoryTabs = () => import("./routes/story/service-story-tabs");

// Screens download the first time they are opened (ADR-040, BND-01). The
// route table below stays eager: paths, params, validateSearch, redirects.
const ServiceCreatePage = lazyScreen(
  () => import("./routes/service-create"),
  "ServiceCreatePage",
);
const ServiceListPage = lazyScreen(
  () => import("./routes/service-list"),
  "ServiceListPage",
);
const ServiceStoryDetail = lazyScreen(
  () => import("./routes/story/service-story-detail"),
  "ServiceStoryDetail",
);
const ServiceActivityRoute = lazyScreen(
  loadServiceStoryTabs,
  "ServiceActivityRoute",
);
const ServiceNotesRoute = lazyScreen(
  loadServiceStoryTabs,
  "ServiceNotesRoute",
);
const ServiceOverviewRoute = lazyScreen(
  loadServiceStoryTabs,
  "ServiceOverviewRoute",
);
const ServiceRelatedRoute = lazyScreen(
  loadServiceStoryTabs,
  "ServiceRelatedRoute",
);

export const createRoutes: CreateModuleRoutes = (parentRoute: AnyRoute) => {
  const listRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "services",
    // The list's filter/search/highlight are URL-borne so back-navigation
    // from the story page restores them (ADR-0081 §3). validateSearch keeps
    // them typed + reload-proof.
    validateSearch: (raw: Record<string, unknown>): ServiceListSearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["highlight"] === "string" && raw["highlight"]
        ? { highlight: raw["highlight"] }
        : {}),
      // POP-01 (ADR-0229): the record open in the pop-up, so a reload
      // reopens it and the browser's back button closes it.
      ...(typeof raw["open"] === "string" && raw["open"]
        ? { open: raw["open"] }
        : {}),
      // POP-02 (ADR-0230): the pop-up's open tab.
      ...(typeof raw["tab"] === "string" && raw["tab"]
        ? { tab: raw["tab"] }
        : {}),
    }),
    component: ServiceListPage,
  });

  const createRouteEntry = createRoute({
    getParentRoute: () => parentRoute,
    path: "services/new",
    component: ServiceCreatePage,
  });

  // ── Story-page detail (ADR-0081): parent services/$id shell + routed
  //    tab children + an index redirect to …/overview. ────────────────────
  const detailRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "services/$id",
    // The list's filter/search + the originating row id ride into the detail
    // route so Prev/Next walks the SAME filtered set and back restores state.
    validateSearch: (raw: Record<string, unknown>): ServiceStorySearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["highlight"] === "string" && raw["highlight"]
        ? { highlight: raw["highlight"] }
        : {}),
    }),
    component: ServiceStoryDetail,
  });

  // Index redirect services/$id → …/overview (the default tab).
  const detailIndexRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "/",
    beforeLoad: ({ params }) => {
      throw redirect({
        to: "/$tenantSlug/services/$id/overview",
        params: params as never,
        replace: true,
      });
    },
  });

  const overviewRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "overview",
    component: ServiceOverviewRoute,
  });
  const relatedRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "related",
    component: ServiceRelatedRoute,
  });
  const notesRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "notes",
    component: ServiceNotesRoute,
  });
  const activityRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "activity",
    component: ServiceActivityRoute,
  });

  (detailRoute as unknown as { addChildren: (c: unknown) => void }).addChildren(
    [detailIndexRoute, overviewRoute, relatedRoute, notesRoute, activityRoute],
  );

  return [listRoute, createRouteEntry, detailRoute];
};

/** List search params (filter/search/highlight) — URL-borne for restore. */
export interface ServiceListSearch {
  q?: string;
  filter_status?: string;
  highlight?: string;
  /** POP-01: the record open in the pop-up over the list. */
  open?: string;
  /** POP-02: the pop-up's open tab (absent: Overview). */
  tab?: string;
}
